import * as React from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, Eye, EyeOff, FilePlus2, Trash2 } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toaster';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { SectionCard } from './SectionCard';
import { PdfFrame } from './PdfFrame';
import { NewProfileDialog } from './NewProfileDialog';
import {
  describeChecks,
  downloadFile,
  toCheckLists,
  useDeleteProfileVersion,
  usePdfObjectUrl,
  versionTag,
} from './api';
import type { CandidateDetail, ProfileVersion } from './types';

export interface ProfileVersionsPanelProps {
  candidate: CandidateDetail;
  canEdit: boolean;
  isAdmin: boolean;
  onChanged: () => void;
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * Generated profile versions (vNN, language, position, pages, checks) with inline PDF preview,
 * download and Drive links, plus the "New profile" generator dialog. Writers can delete an older
 * version; the current profile of a submission and the only version of a candidate stay.
 */
export function ProfileVersionsPanel({ candidate, canEdit, isAdmin, onChanged }: ProfileVersionsPanelProps) {
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const [newOpen, setNewOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<ProfileVersion | null>(null);
  const remove = useDeleteProfileVersion();
  const fileById = React.useMemo(
    () => new Map(candidate.profileFiles.map((f) => [f.id, f])),
    [candidate.profileFiles],
  );
  const versions = candidate.profileVersions;

  const download = async (v: ProfileVersion) => {
    if (!v.pdfFileId) return;
    const file = fileById.get(v.pdfFileId);
    try {
      await downloadFile(
        v.pdfFileId,
        file?.name ?? `${candidate.candidateId}_CandidateProfile_${v.lang}_${versionTag(v.version)}.pdf`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Download failed');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const tag = `${versionTag(toDelete.version)} (${toDelete.lang})`;
    try {
      await remove.mutateAsync(toDelete.id);
      setToDelete(null);
      if (previewId === toDelete.id) setPreviewId(null);
      toast.success(`Profile ${tag} deleted`);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, `Could not delete profile ${tag}`));
    }
  };

  return (
    <SectionCard
      title="Profile versions"
      description="Anonymised candidate profiles (2 pages, no rate, no contact data)."
      actions={
        canEdit ? (
          <Button size="sm" onClick={() => setNewOpen(true)}>
            <FilePlus2 aria-hidden="true" /> New profile
          </Button>
        ) : null
      }
    >
      {versions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No profile generated yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <caption className="sr-only">Profile versions</caption>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Version</TableHead>
                <TableHead>Lang</TableHead>
                <TableHead>Position</TableHead>
                <TableHead className="text-right">Pages</TableHead>
                <TableHead>Checks</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((v) => {
                const checks = toCheckLists(v.checkResults);
                const pdf = v.pdfFileId ? fileById.get(v.pdfFileId) : undefined;
                const driveLink =
                  pdf?.webViewLink ?? (v.docxFileId ? fileById.get(v.docxFileId)?.webViewLink : null);
                const isPreview = previewId === v.id;
                return (
                  <React.Fragment key={v.id}>
                    <TableRow className="hover:bg-transparent">
                      <TableCell className="font-medium tabular-nums">
                        {versionTag(v.version)}
                        {v.forced ? (
                          <SimpleTooltip
                            label={v.forceReason ? `Forced: ${v.forceReason}` : 'Saved with forced checks'}
                          >
                            <Badge variant="muted" className="ml-2">
                              forced
                            </Badge>
                          </SimpleTooltip>
                        ) : null}
                      </TableCell>
                      <TableCell>{v.lang}</TableCell>
                      <TableCell>
                        {v.position ? (
                          <Link to={`/positions/${v.position.id}`} className="hover:underline">
                            <span className="tabular-nums">{v.position.requestNo}</span> {v.position.role}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">Generic</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{v.pageCount ?? '–'}</TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'text-xs',
                            checks.errors.length
                              ? 'text-destructive'
                              : checks.warnings.length
                                ? 'text-warning'
                                : 'text-success',
                          )}
                          title={
                            [...checks.errors, ...checks.warnings].map((c) => c.message).join('\n') ||
                            undefined
                          }
                        >
                          {describeChecks(checks)}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDateTime(v.createdAt)}
                        {v.createdBy ? ` · ${v.createdBy}` : ''}
                        {v.changeNote ? (
                          <div className="max-w-[16rem] truncate" title={v.changeNote}>
                            {v.changeNote}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={
                              isPreview
                                ? `Hide preview of ${versionTag(v.version)}`
                                : `Preview ${versionTag(v.version)}`
                            }
                            aria-pressed={isPreview}
                            disabled={!v.pdfFileId}
                            onClick={() => setPreviewId(isPreview ? null : v.id)}
                          >
                            {isPreview ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Download ${versionTag(v.version)} as PDF`}
                            disabled={!v.pdfFileId}
                            onClick={() => void download(v)}
                          >
                            <Download aria-hidden="true" />
                          </Button>
                          {driveLink ? (
                            <a
                              href={driveLink}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`Open ${versionTag(v.version)} in Drive`}
                              className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
                            >
                              <ExternalLink aria-hidden="true" />
                            </a>
                          ) : null}
                          {canEdit ? (
                            <DeleteVersionButton
                              version={v}
                              blockedReason={
                                v.isCurrent
                                  ? 'The current profile of a submission cannot be deleted'
                                  : versions.length <= 1
                                    ? 'The only profile of a candidate cannot be deleted'
                                    : null
                              }
                              onClick={() => setToDelete(v)}
                            />
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isPreview && v.pdfFileId ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={7} className="bg-muted/30 p-3">
                          <VersionPreview
                            fileId={v.pdfFileId}
                            title={`Preview of ${versionTag(v.version)}`}
                          />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={toDelete !== null} onOpenChange={(open) => (open ? undefined : setToDelete(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Delete {toDelete ? `${versionTag(toDelete.version)} (${toDelete.lang})` : 'this version'}?
            </DialogTitle>
            <DialogDescription>
              The version and its generated files (PDF and JSON, including the Drive copies) are removed.
              Later versions keep their numbers; the next profile continues counting. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" loading={remove.isPending} onClick={() => void confirmDelete()}>
              Delete version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NewProfileDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        candidate={candidate}
        isAdmin={isAdmin}
        onSaved={() => {
          setNewOpen(false);
          onChanged();
        }}
      />
    </SectionCard>
  );
}

/** Trash icon per row; a blocked version keeps the button, disabled, with the reason as tooltip. */
function DeleteVersionButton({
  version,
  blockedReason,
  onClick,
}: {
  version: ProfileVersion;
  blockedReason: string | null;
  onClick: () => void;
}) {
  const label = `Delete ${versionTag(version.version)}`;
  if (blockedReason) {
    return (
      <SimpleTooltip label={blockedReason}>
        {/* A disabled button fires no pointer events, so the span carries the tooltip. */}
        <span tabIndex={0} className="inline-flex" aria-label={blockedReason}>
          <Button variant="ghost" size="icon-sm" aria-label={label} disabled title={blockedReason}>
            <Trash2 aria-hidden="true" />
          </Button>
        </span>
      </SimpleTooltip>
    );
  }
  return (
    <Button variant="ghost" size="icon-sm" aria-label={label} onClick={onClick}>
      <Trash2 aria-hidden="true" />
    </Button>
  );
}

function VersionPreview({ fileId, title }: { fileId: string; title: string }) {
  const { url, error, loading } = usePdfObjectUrl(fileId);
  return <PdfFrame src={url} title={title} loading={loading} error={error} />;
}
